import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Containment for render-time crashes.
 *
 * Without this, one malformed field anywhere in a result unmounts the entire
 * React tree and the analyst gets a white page -- losing the notebook, the
 * catalog and whatever they were mid-way through investigating. A bad value
 * should cost one cell, not the session.
 *
 * Placed around each notebook cell and around each side panel, so a failure is
 * scoped to the smallest thing that can still be described honestly.
 */

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** Shown in the fallback, e.g. "Cell 2" or "Column Explorer". */
  readonly label: string;
  /** Changing any entry resets the boundary -- typically the cell's query id. */
  readonly resetKey?: string | number;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    // A new result should get a clean attempt rather than staying broken.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.label}] render failed:`, error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="error-boundary"
        className="m-2 overflow-hidden rounded-sm border border-danger/50 bg-danger-dim"
      >
        <div className="flex items-center gap-2 border-b border-danger/30 px-3 py-1.5">
          <span className="eyebrow text-danger">{this.props.label} could not be displayed</span>
          <button type="button" className="btn ml-auto" onClick={this.retry}>
            Try again
          </button>
        </div>
        <div className="px-3 py-2">
          <p className="font-mono text-xs leading-5 text-ink">{error.message}</p>
          <p className="mt-1 text-xs text-muted">
            The rest of the workbench is unaffected. Re-running the query usually clears this.
          </p>
        </div>
      </div>
    );
  }
}
